import dayjs from 'dayjs';

const STORAGE_KEY = 'storeTimezone';
const FALLBACK_TIMEZONE = 'UTC';

/**
 * 校验时区标识符是否为 IANA 合法时区
 */
function isValidTimezone(tz: string): boolean {
  if (!tz) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/**
 * 获取浏览器自动检测时区，若不可用则回退 UTC
 */
export function getBrowserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return isValidTimezone(tz) ? tz : FALLBACK_TIMEZONE;
  } catch {
    return FALLBACK_TIMEZONE;
  }
}

/**
 * 获取当前门店时区
 * - 优先 localStorage 中缓存的门店时区（仅当合法时）
 * - 其次回退浏览器自动检测时区
 * - 最终回退 UTC
 */
export function getStoreTimezone(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && isValidTimezone(saved)) {
    return saved;
  }
  if (saved) {
    // 缓存的时区已失效（如 Iraq/Duhok），清理避免反复触发错误
    localStorage.removeItem(STORAGE_KEY);
  }
  return getBrowserTimezone();
}

/**
 * 设置门店时区到 localStorage
 * - 仅写入合法 IANA 时区，非法值会被忽略并清理旧缓存
 */
export function setStoreTimezone(tz: string): void {
  if (isValidTimezone(tz)) {
    localStorage.setItem(STORAGE_KEY, tz);
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * 根据门店时区获取默认日期时间格式
 * - 亚洲时区 → 24 小时制中式格式
 * - 其他地区 → 12 小时制美式格式
 */
function getDefaultDateTimeFormat(): string {
  const tz = getStoreTimezone();
  if (tz.startsWith('Asia/')) {
    return 'YYYY-MM-DD HH:mm:ss';
  }
  return 'MM/DD/YYYY, h:mm A';
}

/**
 * UTC 时间转门店时区显示
 * @param utcTime - UTC 时间字符串（如 2026-04-02T10:30:00Z）
 * @param formatStr - 自定义格式，不传则按门店时区自动选择
 * @returns 格式化后的本地时间字符串
 */
export function formatDateTime(utcTime?: string | null, formatStr?: string): string {
  if (!utcTime) return '—';
  const tz = getStoreTimezone();
  const d = dayjs.utc(utcTime).tz(tz);
  if (!d.isValid()) return String(utcTime);
  const fmt = formatStr || getDefaultDateTimeFormat();
  return d.format(fmt);
}

/**
 * 根据门店时区获取默认日期格式
 * - 亚洲时区 → YYYY-MM-DD
 * - 其他地区 → MM/DD/YYYY
 */
function getDefaultDateFormat(): string {
  const tz = getStoreTimezone();
  if (tz.startsWith('Asia/')) {
    return 'YYYY-MM-DD';
  }
  return 'MM/DD/YYYY';
}

/**
 * UTC 时间转门店时区日期显示
 * @param utcTime - UTC 时间字符串
 * @returns 格式化后的本地日期字符串（格式按门店时区自动选择）
 */
export function formatDate(utcTime?: string | null): string {
  if (!utcTime) return '—';
  const tz = getStoreTimezone();
  const d = dayjs.utc(utcTime).tz(tz);
  if (!d.isValid()) return String(utcTime);
  return d.format(getDefaultDateFormat());
}

/**
 * 本地日期字符串（YYYY-MM-DD）转 UTC 起止时间范围
 * 用于日期筛选查询传给后端
 * @param localDateStr - 本地日期字符串 YYYY-MM-DD
 * @returns {{ start: string, end: string }} UTC ISO 8601 起止时间
 */
export function localDateToUtcRange(localDateStr: string): { start: string; end: string } {
  const tz = getStoreTimezone();
  const start = dayjs.tz(localDateStr, tz).startOf('day').utc().toISOString();
  const end = dayjs.tz(localDateStr, tz).endOf('day').utc().toISOString();
  return { start, end };
}

/**
 * 将本地日期字符串转为 UTC 日期字符串（保持日期不变）
 * 用于 birth_date / measured_at 等纯日期字段的表单提交
 * @param localDateStr - 本地日期 YYYY-MM-DD
 * @returns UTC 日期 YYYY-MM-DD
 */
export function localDateToUtcDate(localDateStr: string): string {
  const tz = getStoreTimezone();
  return dayjs.tz(localDateStr, tz).utc().format('YYYY-MM-DD');
}
